import { useEffect, useState } from 'react';
import { attractAudio, audioAsset } from '../lib/attractAudio';
import { nextVoiceSpeed, useAudioPreferences } from '../lib/audioPreferences';

interface CharacterSpeakButtonProps {
  characterId: string;
  characterName: string;
}

export function CharacterSpeakButton({ characterId, characterName }: CharacterSpeakButtonProps) {
  const [speaking, setSpeaking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [volumeOpen, setVolumeOpen] = useState(false);
  const [preferences, updatePreferences] = useAudioPreferences();

  useEffect(() => {
    setSpeaking(false);
    setLoading(false);
    return () => attractAudio.stopManualVoice();
  }, [characterId]);

  const toggle = async () => {
    if (loading) return;
    setLoading(true);
    const active = await attractAudio.toggleManualVoice(
      audioAsset(`/assets/audio/characters/${characterId}.wav`),
      () => setSpeaking(false),
    );
    setSpeaking(active);
    setLoading(false);
  };

  return <div className="character-voice-controls">
    <button className={`character-speak${speaking ? ' is-speaking' : ''}`} type="button" onClick={toggle} aria-label={speaking ? `Stop ${characterName} speaking` : `Speak as ${characterName}`} aria-pressed={speaking} title="Speak" disabled={loading}>
      <svg viewBox="0 0 16 16" aria-hidden="true">{speaking ? <rect x="4" y="4" width="8" height="8" /> : <path d="M5 3.5 12 8l-7 4.5z" />}</svg>
    </button>
    <button className="character-voice-volume" type="button" aria-label="Character voice volume" aria-expanded={volumeOpen} title="Character voice volume" onClick={() => setVolumeOpen((current) => !current)}>
      <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 6h3l3-3v10l-3-3H2zm8.2-1.7a5 5 0 0 1 0 7.4l-1-1a3.6 3.6 0 0 0 0-5.4z" /></svg>
    </button>
    {volumeOpen && <label className="character-voice-slider"><span>{Math.round(preferences.characterVolume * 100)}%</span><input type="range" min="0" max="1.5" step="0.05" value={preferences.characterVolume} onChange={(event) => updatePreferences({ characterVolume: Number(event.target.value) })} aria-label="Character voice volume level" /></label>}
    <button className="character-voice-speed" type="button" aria-label={`Character voice speed ${preferences.characterSpeed} times. Activate for next speed.`} title="Character voice speed" onClick={() => updatePreferences({ characterSpeed: nextVoiceSpeed(preferences.characterSpeed) })}>{preferences.characterSpeed}×</button>
  </div>;
}
